import * as React from "react";
import type { CSSProperties, ReactNode } from "react";
import { Box } from "../components/ui/box.tsx";
import { Icon, type IconName } from "../components/ui/icon.tsx";
import { Text } from "../components/ui/text.tsx";
import { Tooltip, TooltipPopup, TooltipTrigger } from "../components/ui/tooltip.tsx";
import { labelControl } from "../lib/label-control.tsx";
import type { Omittable } from "../lib/props.ts";
import { ROW_RUNGS } from "../lib/row-rungs.ts";

export type PropertyRowProps = Omittable<{
  icon: IconName;
  label: string;
  required?: boolean;
  error?: string;
  /** DOM id for navigation to the row. */
  id?: string;
  children: ReactNode;
}>;

/** The label rail: fluid between 88px and 160px, 36% of the row — at a
 *  side panel's width it holds a two-word label whole, also in a font wider
 *  than the Mac system font (Linux's DejaVu Sans).
 *  A list that sits under property rows reads the parts to put its values
 *  on their column. */
export const PROPERTY_ROW_LABEL_RAIL = { minPx: 88, percent: 36, maxPx: 160 } as const;
const PROPERTY_ROW_LABEL_WIDTH = `clamp(${PROPERTY_ROW_LABEL_RAIL.minPx}px, ${PROPERTY_ROW_LABEL_RAIL.percent}%, ${PROPERTY_ROW_LABEL_RAIL.maxPx}px)`;

/** A compact property label and a flexible value slot. */
export function PropertyRow({
  icon,
  label,
  required,
  error,
  id,
  children,
}: PropertyRowProps) {
  const labelId = React.useId();
  return (
    // ROW_RUNGS.propertyRow pitch — the height of the 32px value control the
    // row hosts, so a stack of rows reads as a column of controls.
    // min-width 0 on the row too: as a grid item (PropertyGrid) a row's
    // automatic minimum is its content, and one long value would push the row
    // past its column.
    <Box
      id={id}
      direction="row"
      align="center"
      gap="sm"
      height={ROW_RUNGS.propertyRow}
      minWidth={0}
    >
      {/* The label rail rides the row's width — 36% of it, between 88px and
          160px — so a narrowing host shrinks the
          label and the value together. A label longer than the rail ends
          in an ellipsis instead of wrapping inside the fixed-height row. */}
      <Box
        id={labelId}
        direction="row"
        align="center"
        gap="sm"
        width={PROPERTY_ROW_LABEL_WIDTH}
        shrink={false}
        minWidth={0}
      >
        <Icon name={icon} size="md" color={error ? "destructive" : "muted"} />
        <Text
          size="sm"
          weight="normal"
          variant={error ? "destructive" : "muted"}
          truncate={1}
        >
          {label}
          {required && <Text variant="destructive"> *</Text>}
        </Text>
        {error ? (
          // The tooltip trigger renders its own element, so the icon gets a Box
          // host rather than becoming a button nobody can act on.
          <Tooltip>
            <TooltipTrigger render={<Box direction="row" align="center" />}>
              <Icon name="alert-circle" size="md" color="destructive" />
            </TooltipTrigger>
            <TooltipPopup side="right">
              <Text size="xs">{error}</Text>
            </TooltipPopup>
          </Tooltip>
        ) : null}
      </Box>
      {/* min-width 0: `flex-1` alone floors at content width, so a wide value
          (a one-line address) would stretch the row past the panel.
          Value controls are squared: the slot pins unnamed Buttons to the
          rounded-md rung. An explicit `shape` keeps its geometry because
          Button stamps `data-shape="default"` only when the call site leaves
          the shape unnamed. Popups are portaled and exempt.
          (DataTable rows carry the same descendant rule.) */}
      {/* The value slot is a group named by the label. A control that takes
          `aria-labelledby` is named by it directly (labelControl); a composite
          one — a picker whose trigger shows its value, "Todo" — keeps its own
          name, and the group says which property it is: "Stage, group". */}
      <Box
        fill="flex"
        minWidth={0}
        // A column so the value control stretches across the slot — its
        // hover and selected wash spans the row, not just its text. The slot
        // takes the row's full height so a `fill="flex"` control (the
        // address cell) has a height to fill instead of collapsing to 0.
        direction="column"
        alignSelf="stretch"
        justify="center"
        role="group"
        aria-labelledby={labelId}
        className="[&_[data-shape=default]]:rounded-md [&_[data-shape=default]]:before:rounded-md"
      >
        {labelControl(children, labelId)}
      </Box>
    </Box>
  );
}

/** Narrowest column a PropertyGrid lays out: the label rail + 8px gap + a
 *  value the width of a short one-line address. */
export const PROPERTY_GRID_COLUMN_MIN_WIDTH = 360;
/** Gutter between PropertyGrid columns. */
export const PROPERTY_GRID_COLUMN_GAP = 24;
/** Pitch between rows: the 32px row plus this reads as one list of controls. */
const PROPERTY_GRID_ROW_GAP = 6;

export type PropertyGridProps = Omittable<{
  /** Narrowest a column may be, in px. Second column appears at
   *  `2 * columnMinWidth + PROPERTY_GRID_COLUMN_GAP` of the grid's OWN width. */
  columnMinWidth?: number;
  children: ReactNode;
}>;

/**
 * Lays PropertyRows into as many equal columns as its own width holds at
 * `columnMinWidth`, rows flowing left to right. Its ruler is its own box, not
 * the viewport, so one call site is a single column in a narrow pane and two
 * columns in a wide page. The minimum caps at the grid's width, so a column
 * never overflows a host narrower than `columnMinWidth`.
 */
export function PropertyGrid({
  columnMinWidth = PROPERTY_GRID_COLUMN_MIN_WIDTH,
  children,
}: PropertyGridProps) {
  const style: CSSProperties = {
    gridTemplateColumns: `repeat(auto-fill, minmax(min(100%, ${columnMinWidth}px), 1fr))`,
  };
  return (
    <Box
      display="grid"
      gapX={PROPERTY_GRID_COLUMN_GAP}
      gapY={PROPERTY_GRID_ROW_GAP}
      style={style}
    >
      {children}
    </Box>
  );
}
